import { useEffect, useState } from 'react';
import { aTextoCentinelas, compararCentinelas, leerCentinelas } from './emailSentinels';
import { useGuardarConfiguracion } from './useGuardarConfiguracion';
import { ConfigCard, AvisoDeGuardado } from './ConfigCard';

/**
 * Los **correos ignorados** de SuccessFactors: el texto que el origen pone en el campo de correo para
 * decir «esta persona no tiene correo» (`no_mail@sigma-alimentos.com` y compañía).
 *
 * 🔴 Hasta ahora esta lista vivía **escrita a mano en el código**: ocho valores elegidos a ojo que
 * negocio no podía tocar sin un despliegue. Y el precio de que falte uno está medido — el 2026-08-07 un
 * centinela tratado como dirección real hizo que el índice único parcial `TenantId_1_Email_1` muriera
 * con `E11000` en el segundo `BulkWrite`, y se llevó **130 000 filas**.
 *
 * Mismo flujo que el catálogo de dominios (validar → enseñar el diff → confirmar en dos pasos) porque
 * el riesgo es de la misma familia: **quitar** una entrada es lo peligroso.
 */
export function SentinelsCard() {
  const { t, data, isLoading, isError, guardando, guardar, aviso, mensajeDeError } = useGuardarConfiguracion();

  const [texto, setTexto] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  // Se resincroniza desde el servidor SÓLO mientras nadie lo esté editando: sin esa condición, un
  // refetch de react-query —al recuperar el foco de la pestaña— borraría lo escrito a medias.
  const guardados = data?.successFactors.emailSentinelLocalParts;
  useEffect(() => {
    if (guardados && texto === null) setTexto(aTextoCentinelas(guardados));
  }, [guardados, texto]);

  if (isLoading) return <p className="jobs-card-hint" role="status">{t('common.loading')}</p>;
  if (isError || !data) return <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.loadError')}</p>;

  const leidos = leerCentinelas(texto ?? '');
  const cambio = compararCentinelas(data.successFactors.emailSentinelLocalParts ?? [], leidos.centinelas);
  const hayCambios = cambio.anadidos.length > 0 || cambio.quitados.length > 0;

  const guardarCentinelas = () => {
    setConfirmando(false);
    guardar(
      { emailSentinelLocalParts: leidos.centinelas },
      t('jobs.sync.sentinelsSaved'),
      () => setTexto(null),
    );
  };

  return (
    <ConfigCard
      icono="fa-envelope-open-text"
      titulo={t('jobs.sync.sentinels')}
      descripcion={t('jobs.sync.sentinelsDesc')}
      pie={
        <>
          <AvisoDeGuardado aviso={aviso} error={mensajeDeError} />
          {data.updatedBy && (
            <p className="jobs-card-meta">{t('jobs.sync.updatedBy')} {data.updatedBy}</p>
          )}
        </>
      }
    >
      <fieldset className="jobs-fieldset" disabled={guardando}>
        <label className="jobs-field-label" htmlFor="emailSentinels">
          {t('jobs.sync.sentinelsHint')}
        </label>
        <textarea
          id="emailSentinels"
          className="jobs-textarea"
          value={texto ?? ''}
          onChange={e => { setTexto(e.target.value); setConfirmando(false); }}
          rows={10}
          spellCheck={false}
          autoCapitalize="none"
        />

        <p className="jobs-card-meta">{t('jobs.sync.sentinelsCount', { n: leidos.centinelas.length })}</p>

        {leidos.problemas.length > 0 && (
          <ul
            className={`jobs-problem-list jobs-alert ${
              leidos.problemas.every(p => p.tipo === 'normalizado') ? 'jobs-alert--info' : 'jobs-alert--bad'
            }`}
            role={leidos.problemas.some(p => p.tipo !== 'normalizado') ? 'alert' : 'status'}
          >
            {leidos.problemas.map((problema, i) => (
              <li key={`${problema.tipo}-${problema.valor}-${i}`}>
                <code>{problema.valor}</code>{' — '}
                {problema.tipo === 'formato'
                  ? t('jobs.sync.sentinelBadFormat')
                  : problema.tipo === 'duplicado'
                    ? t('jobs.sync.domainDuplicate')
                    /* 🔴 No es un error: se acepta y se avisa, porque el efecto es MÁS AMPLIO de lo que
                       la persona escribió. Pegar `nomail@sigma-alimentos.com` ignora también
                       `nomail@cualquier-otro.com`, y eso hay que verlo antes de guardar. */
                    : t('jobs.sync.sentinelNormalized', { valor: problema.comoQueda })}
              </li>
            ))}
          </ul>
        )}

        {hayCambios && (
          <div className="jobs-diff">
            {cambio.quitados.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsRemoved')}:</strong>{' '}
                <code>{cambio.quitados.join(', ')}</code>
                <small>{t('jobs.sync.sentinelsRemovedWarning')}</small>
              </p>
            )}
            {cambio.anadidos.length > 0 && (
              <p>
                <strong>{t('jobs.sync.domainsAdded')}:</strong>{' '}
                <code>{cambio.anadidos.join(', ')}</code>
                <small>{t('jobs.sync.sentinelsAddedWarning')}</small>
              </p>
            )}
          </div>
        )}

        <div className="jobs-card-actions">
          {!confirmando ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!hayCambios || leidos.centinelas.length === 0 || guardando}
              onClick={() => setConfirmando(true)}
            >
              {t('jobs.sync.domainsReview')}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn--primary btn--sm" onClick={guardarCentinelas} disabled={guardando}>
                {t('jobs.sync.domainsConfirm')}
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => setConfirmando(false)} disabled={guardando}>
                {t('jobs.sync.domainsCancel')}
              </button>
            </>
          )}
        </div>

        {/* 🔴 La lista vacía la rechaza el servidor (con motivo: sin centinelas vuelve el E11000), así
            que la pantalla lo dice ANTES de intentarlo en vez de dejar que llegue un 400. */}
        {leidos.centinelas.length === 0 && (
          <p className="jobs-alert jobs-alert--bad" role="alert">{t('jobs.sync.sentinelsEmpty')}</p>
        )}
      </fieldset>
    </ConfigCard>
  );
}
